// Regions for the mesh engines: whether a world point is in one, which cells some points fill, and the test an
// engine meshes by (in its region, in none of the regions earlier parts of a plan took). Pure.
import { unitRows } from '../volumes.ts';
import { blockKey } from './blockgrid.ts';
import { KeyMap } from './hash.ts';
import type { FrameMasks, MeshFrame, Region } from './types.ts';

type Test = (x: number, y: number, z: number) => boolean;

/** The region's membership test; null: everywhere. */
export function regionTest(r: Region): Test | null {
  if (r.kind === 'all') return null;
  if (r.kind === 'boxes') {
    const rows = r.vols.map(unitRows);
    return (x, y, z) => {
      for (const q of rows)
        if (Math.abs(q[0] * x + q[1] * y + q[2] * z + q[3]) <= 1 && Math.abs(q[4] * x + q[5] * y + q[6] * z + q[7]) <= 1 &&
            Math.abs(q[8] * x + q[9] * y + q[10] * z + q[11]) <= 1) return true;
      return false;
    };
  }
  const s = r.cell, cells = r.cells;
  return (x, y, z) => cells.get(blockKey(Math.floor(x / s), Math.floor(y / s), Math.floor(z / s))) >= 0;
}

/** In r and in none of exclude; null: everywhere. */
export function keepTest(r: Region, exclude: Region[] = []): Test | null {
  const inR = regionTest(r), out = exclude.map(regionTest).filter((t): t is Test => t !== null);
  if (exclude.some((e) => e.kind === 'all')) return () => false;
  if (!inR && !out.length) return null;
  return (x, y, z) => (!inR || inR(x, y, z)) && !out.some((t) => t(x, y, z));
}

/** The cells of size `cell` holding points P (xyz), grown by `grow` cells each way. */
export function cellsOf(P: ArrayLike<number>, cell: number, grow = 1): Region {
  const cells = new KeyMap(1024), n = P.length / 3;
  const seen = new KeyMap(1024);
  for (let i = 0; i < n; i++) {
    const cx = Math.floor(P[3 * i] / cell), cy = Math.floor(P[3 * i + 1] / cell), cz = Math.floor(P[3 * i + 2] / cell);
    const k = blockKey(cx, cy, cz);
    if (seen.get(k) >= 0) continue;
    seen.set(k, 1);
    for (let dz = -grow; dz <= grow; dz++) for (let dy = -grow; dy <= grow; dy++) for (let dx = -grow; dx <= grow; dx++) cells.set(blockKey(cx + dx, cy + dy, cz + dz), 1);
  }
  return { kind: 'cells', cell, cells };
}

/**
 * Depth pixel masks from points: each frame's own points (world P, as the frames are placed; frame: their frame ids,
 * scan: their scan) projected into its depth grid, `pick` points marking theirs with a pixel round them. invert: the
 * mask is every pixel but those (the undeleted points' pixels: all but the deleted ones').
 */
export function frameMasks(frames: MeshFrame[], scan: string, P: Float32Array, frame: Uint32Array, pick: (i: number) => boolean, invert = false): FrameMasks {
  const byFrame = new Map<number, number[]>();
  for (let i = 0; i < frame.length; i++) if (pick(i)) (byFrame.get(frame[i]) ?? byFrame.set(frame[i], []).get(frame[i])!).push(i);
  const out: FrameMasks = new Map();
  for (const fr of frames) {
    if (fr.scan !== scan) continue;
    const c = fr.cam, m = new Uint8Array(c.dw * c.dh).fill(invert ? 1 : 0), pts = byFrame.get(fr.f);
    if (!pts && !invert) continue;
    const R = c.P.R, t = c.P.t, [fx, fy, cx, cy] = c.Kd;
    for (const i of pts ?? []) {
      const dx = P[3 * i] - t[0], dy = P[3 * i + 1] - t[1], dz = P[3 * i + 2] - t[2];
      const qz = R[2] * dx + R[5] * dy + R[8] * dz;
      if (qz <= 0) continue;
      const u = Math.round((fx * (R[0] * dx + R[3] * dy + R[6] * dz)) / qz + cx), v = Math.round((fy * (R[1] * dx + R[4] * dy + R[7] * dz)) / qz + cy);
      for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) {
        const uu = u + k, vv = v + j;
        if (uu >= 0 && vv >= 0 && uu < c.dw && vv < c.dh) m[vv * c.dw + uu] = invert ? 0 : 1;
      }
    }
    out.set(fr.key, m);
  }
  return out;
}

/** Masks of different scans (or frames) in one. */
export function anyMasks(...ms: FrameMasks[]): FrameMasks {
  const out: FrameMasks = new Map();
  for (const m of ms) for (const [k, a] of m) out.set(k, a);
  return out;
}

/** Pixels both masks allow (a frame missing from either: left out); undefined masks allow everything. */
export function bothMasks(a?: FrameMasks, b?: FrameMasks): FrameMasks | undefined {
  if (!a || !b) return a ?? b;
  const out: FrameMasks = new Map();
  for (const [k, x] of a) {
    const y = b.get(k);
    if (!y) continue;
    const m = x.slice();
    for (let i = 0; i < m.length; i++) m[i] &= y[i];
    out.set(k, m);
  }
  return out;
}
